import React, { Component, Fragment } from 'react';
import isEqual from 'lodash/isEqual';
import params from '../../utils/queryParams';

import {
  numberWithCommas,
  debounce,
  getNestedValue,
  checkNestedKeys
} from '../../utils/utils';

import CSVExport from '../CSVExport/CSVExport';
import SearchInput from '../SearchInput/SearchInput';
import HeaderInfoTooltip from '../HeaderInfoTooltip/HeaderInfoTooltip';
import TablePagination from '../TablePagination/TablePagination';

import FilterBox from './FilterBox';

const PAGE_SIZE = 50;
const DEFAULT_CONTENT_MIN_WIDTH = 1600;

export class DataTable extends Component {
  constructor(props) {
    super(props);

    const si = props.searchInput || {};
    // if there is an incoming search token, and the table name matches, set it
    const searchQuery =
      si.searchTerm && si.tableName === props.tableName ? si.searchTerm : '';

    let qpSearch = params.get('s');
    this.state = {
      needToSort: true,
      originalData: [],
      searchQuery: qpSearch ? decodeURIComponent(qpSearch) : searchQuery,
      searchedData: [],
      sortedData: [],
      pageNumber: 1,
      sortColumn: props.queryParams['sort_col'] || props.defaultSort,
      sortDirection:
        props.queryParams['sort_dir'] || props.defaultSortDirection,
      shouldSendAnalytics: false,
      isFiltered: false,
      totalCount: null
    };
  }

  componentDidMount() {
    this.setWidths();

    const { searchInput, tableName } = this.props;
    const sitName = (searchInput && searchInput.tableName) || '';
    // reset the cached search token if this is not either THE table, or its child details table
    if (sitName !== tableName && `${sitName}_details` !== tableName) {
      // reset values if the tables are different
      this.props.searchInputFunction('', tableName);
    }
    this.filterData();
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, columns } = this.props;
    const prevData = prevProps.data;
    if (!isEqual(data, prevData)) {
      if (!data || !data.length > 0) {
        this.setState({
          originalData: [],
          searchedData: [],
          sortedData: [],
          displayData: null
        });
      } else {
        this.setState(
          {
            pageNumber: 1,
            originalData: data,
            needToSort: true
          },
          () => {
            this.filterData();
          }
        );
      }
    }
    if (!isEqual(columns, prevProps.columns)) {
      this.setWidths();
    }
  }

  setWidths = () => {
    const { columns, minTablePixelWidth } = this.props;

    // we get the width of the .Table element. subtract the widths of all the set width columns.
    // then we distribute the rest.
    const tableWidth = !!minTablePixelWidth
      ? minTablePixelWidth
      : DEFAULT_CONTENT_MIN_WIDTH;
    let noSetWidthCount = 0;
    const fixedColumnsSum = columns.reduce((accumulator, col) => {
      const strWidth = col.width || 0;
      if (!strWidth) {
        noSetWidthCount++;
        return accumulator;
      }

      // if its a %, calculate by multiplying it by the total width;
      let calculatedWidth = 0;
      if (strWidth && typeof strWidth === 'string') {
        // percent path
        calculatedWidth = parseInt(strWidth.replace(/\D/g, ''));
        if (strWidth.includes('%')) {
          // strip other chars and return number
          calculatedWidth =
            calculatedWidth > 0
              ? Math.floor((calculatedWidth / 100) * tableWidth)
              : 0;
        }
      }

      return accumulator + calculatedWidth;
    }, 0);
    const leftOver = tableWidth - fixedColumnsSum;

    const portion = `${Math.round(
      noSetWidthCount > 0 ? leftOver / noSetWidthCount : 50
    )}px`;

    const newColumns = columns.map((col) => {
      return { ...col, width: col.width || portion };
    });

    this.setState({ columns: newColumns });
  };

  validateImportantProps = () => {
    const {
      ErrorHandler,
      searchColumns,
      columns,
      data,
      defaultSort
    } = this.props;
    if (!data || !data.length) {
      return;
    }

    const sampleRow = data && data.length && data.length > 0 && data[0];

    // Validate column dataKeys
    columns.forEach((col) => {
      if (!sampleRow || !checkNestedKeys(col.dataKey, sampleRow)) {
        ErrorHandler.bug(
          `ERROR::DataTable::dataKey ${col.dataKey} is not valid!`
        );
      }
    });

    // Validate SearchColumns
    if (searchColumns && searchColumns.length > 0) {
      searchColumns.forEach((searchCol) => {
        if (!checkNestedKeys(searchCol, sampleRow)) {
          ErrorHandler.bug(
            `ERROR::DataTable::SearchColumn ${searchCol} is not valid!`
          );
        }
      });
    }

    // Validate defaultSort column only if a defaultSort has been provided
    if (defaultSort && !columns.find((col) => col.dataKey === defaultSort)) {
      ErrorHandler.bug(
        `ERROR::DataTable::defaultSort ${defaultSort} is not valid!`
      );
    }
  };

  //   --initial load--
  //   data ==>> searchFilter ==>> sort ==>> paginate ==>> display
  //   --on search--
  //   data ==>> searchFilter ==>> sort ==>> paginate ==>> display
  //   --on sort--
  //   searchData ==>> sort ==>> paginate ==>> display
  //   --on page--
  //   sortedData ==>> paginate ==>> display

  filterData = () => {
    this.validateImportantProps();

    const { searchColumns, columns } = this.props;
    const { searchQuery, originalData } = this.state;
    // if there is no search term OR if there are no search columns return all originalData
    if (!searchColumns || !searchColumns.length > 0 || !searchQuery) {
      if (!this.state.needToSort) {
        this.setState({
          searchedData: originalData,
          sortedData: originalData
        });
      } else {
        this.setState({ searchedData: originalData }, () => {
          this.handleSort();
        });
      }
      return;
    }

    const searchedData = originalData.filter((row) => {
      let keep = false;
      for (const col of searchColumns) {
        const colConfig = columns.find(
          (c) => c.key === col || c.dataKey === col
        );
        // Here instead of just getting the raw value from the data, we should format it similar to how it appears on
        // the view so that people can search specifically what they are seeing... specially if we are formatting some
        // array or nested values.
        let val =
          colConfig &&
          colConfig.formatter &&
          colConfig.formatter(getNestedValue(col, row), row);

        // if we didn't get a string from formatting it, then fallback to the data value
        if (!val || typeof val !== 'string') {
          val = getNestedValue(col, row);
          // if the value is an array, join it into a string so we can search on it
          val = Array.isArray(val) ? val.join('') : val;
        }

        if (
          val &&
          typeof val === 'string' &&
          val.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          keep = true;
          break;
        }
      }
      return keep;
    });

    if (!this.state.needToSort) {
      this.setState({ searchedData, sortedData: searchedData });
    } else {
      this.setState({ searchedData }, () => {
        this.handleSort();
      });
    }
  };

  prepFieldForSort = (sortType, val, column) => {
    const { ErrorHandler } = this.props;
    let aVal = val;
    switch (sortType) {
      case 'number':
        if (typeof aVal === 'string') {
          aVal = parseFloat(aVal.replace(/[^\d.-]/g, ''));
        }
        if (!aVal) {
          aVal = 0;
        }
        break;
      case 'boolean':
        // should just work
        break;
      case 'string':
        aVal = typeof aVal === 'string' ? aVal.toLowerCase() : aVal;
        break;
      case 'date':
      case 'time':
        aVal = new Date(aVal).getTime();
        break;
      case 'array':
        // if we try to sort a column of arrays, just join the arrays into a big string, and string compare...
        // lets hope they have the values in the order they want. may consider sorting the strings first??
        aVal = Array.isArray(aVal) ? aVal.join('') : '';
        break;
      case 'object':
        // I don't know why anyone would try to sort by objects,
        // but if they do, we will just take the values in order
        // mash them into a string, then compare the strings. lol
        aVal = aVal ? Object.values(aVal).join('') : '';
        ErrorHandler.bug(
          'Attempting to sort a column of objects... are you sure you wired the data up right?'
        );
        break;
      default:
        ErrorHandler.bug(
          `ERROR::DataTable::Invalid type [${sortType}] provided for datatable column [${column}], sample value: ${JSON.stringify(
            aVal
          )}`
        );
    }
    return aVal;
  };

  handleSort = (dataKey) => {
    let column = dataKey;
    const {
      createEvent,
      eventTypes,
      tableName,
      columns,
      secondarySort
    } = this.props;
    const {
      sortColumn,
      sortDirection,
      searchedData,
      shouldSendAnalytics
    } = this.state;

    let direction = sortDirection;

    if (dataKey) {
      // if we are passing a key, then its cause we clicked a header,
      // so we need to handle direction change if needed
      if (dataKey === sortColumn) {
        // if the column passed is the same as the currently sorted column, then switch direction
        direction = direction === 'ASC' ? 'DESC' : 'ASC';
        this.setState({ sortDirection: direction });
      }
      this.setState({ sortColumn: dataKey });
      this.props.setQueryParams({
        ['sort_col']: encodeURIComponent(dataKey),
        ['sort_dir']: encodeURIComponent(direction)
      });
    } else {
      column = sortColumn;
    }

    if (shouldSendAnalytics) {
      // Analytics
      createEvent &&
        typeof createEvent === 'function' &&
        createEvent({
          action: eventTypes && eventTypes.TABLE_SORT,
          detail: `${tableName}/${dataKey}:${direction}`
        });
    }

    // find the item from the columns so that we can find its 'type'
    // also maybe we should allow the user to pass a sort function
    // on second thought, no, lets just keep expanding the sort types. we can have nice custom ones.

    // if dataKey is undefined, then we are doing the default sort, so use that as the key
    const sortItem = columns.find((col) => col.dataKey === column);
    let sortType = sortItem && sortItem.type;

    let sortedData = [];

    //gotchas  null values
    // mixed types?
    sortedData = searchedData.sort((a, b) => {
      let aVal = getNestedValue(column, a) || '';
      let bVal = getNestedValue(column, b) || '';
      let cVal = '';
      let dVal = '';

      if (secondarySort) {
        cVal = getNestedValue(secondarySort, a) || '';
        dVal = getNestedValue(secondarySort, b) || '';
      }

      // if a column sort type was not provided, try to get it manually
      sortType = sortType || typeof aVal;
      if (sortType === 'object') {
        sortType = Array.isArray(aVal) ? 'array' : 'object';
      }

      aVal = this.prepFieldForSort(sortType, aVal, column);
      bVal = this.prepFieldForSort(sortType, bVal, column);
      if (secondarySort) {
        let secondarySortType = typeof cVal;
        if (secondarySortType === 'object') {
          secondarySortType = Array.isArray(aVal) ? 'array' : 'object';
        }
        cVal = this.prepFieldForSort(secondarySortType, cVal, column);
        dVal = this.prepFieldForSort(secondarySortType, dVal, column);
      }

      const compareVals = (a, b, shouldSecondarySort) => {
        let result = 0;

        if (a < b) {
          result = 1;
        } else if (a > b) {
          result = -1;
        } else if (shouldSecondarySort) {
          compareVals(cVal, dVal, false);
        }

        return result;
      };

      return direction === 'DESC'
        ? compareVals(aVal, bVal, secondarySort)
        : compareVals(bVal, aVal, secondarySort);
    });

    this.setState({
      sortedData,
      needToSort: false,
      shouldSendAnalytics: false
    });
  };

  // handleSortOnload = () => {
  //   // sets sort on load depending on parameter
  //   const sort_column = this.props.queryParams['sort_col'];
  //   const sort_direction = this.props.queryParams['sort_dir'];
  //   if (sort_column){
  //     this.setState({ shouldSendAnalytics: true, sortDirection: sort_direction }, () => {
  //       this.handleSort(sort_column);
  //     })
  //   }
  // }

  handleColumnHeaderClick = (dataKey) => {
    this.setState({ shouldSendAnalytics: true }, () => {
      this.handleSort(dataKey);
    });

    // setting query parameter for column sort.
    // this.props.setQueryParams({ ['sort_col']: encodeURIComponent(dataKey), ['sort_dir']: encodeURIComponent(this.state.sortDirection) })
  };

  handlePageChange = (page) => {
    this.setState({ pageNumber: page });
  };

  handleDebouncedSearchQueryAnalyticsLogging = debounce(() => {
    const { tableName, createEvent, eventTypes } = this.props;
    const { searchQuery } = this.state;
    createEvent &&
      typeof createEvent === 'function' &&
      createEvent({
        action: eventTypes && eventTypes.TEXT_FILTER,
        detail: `${tableName}:${searchQuery}`
      });
  }, 1500);

  handleQueryChange = (e, value) => {
    this.handleDebouncedSearchQueryAnalyticsLogging();
    const searchQuery = value;
    if (typeof this.props.setQueryParams === 'function') {
      this.props.setQueryParams({ s: encodeURIComponent(value) });
    }
    this.setState({ searchQuery, pageNumber: 1 }, this.filterData);
  };

  handleRowClick = (row) => {
    if (this.props.onRowClick && typeof this.props.onRowClick === 'function') {
      this.props.searchInputFunction(
        this.state.searchQuery,
        this.props.tableName
      );
      this.props.onRowClick(row);
    }
  };

  handleFilterChange = ({ filteredData, isFiltered, totalCount }) => {
    this.setState({ originalData: filteredData, isFiltered, totalCount }, () =>
      this.filterData()
    );
  };

  render() {
    const {
      sortedData,
      originalData,
      pageNumber,
      sortColumn,
      sortDirection,
      searchQuery,
      widths,
      isFiltered,
      totalCount,
      columns
    } = this.state;

    const {
      pageName,
      csvColumns,
      csvFilterCategories,
      columnTotals,
      className,
      isLoading,
      emptyStateMessage,
      noResultsMessage,
      title,
      subtitle,
      additionalFilters,
      searchColumns,
      tableProps,
      rowProps,
      searchPlaceholder,
      dataType,
      isEmpty,
      minTablePixelWidth,
      data,
      csvCustomFileName,
      filterCategories
    } = this.props;

    let displayData = [];
    let itemCount = 0;

    if (sortedData && sortedData.length > 0) {
      const startIndex = PAGE_SIZE * (pageNumber - 1);
      const endIndex = PAGE_SIZE * pageNumber;
      displayData =
        (sortedData &&
          sortedData.length > 0 &&
          sortedData.slice(startIndex, endIndex)) ||
        [];
      itemCount = sortedData.length;
    }
    const filterCat = !!filterCategories ? filterCategories : [];
    const isNoData =
      isEmpty || !data || !Array.isArray(data) || data.length === 0;

    // if a callback function was passed as a prop for the onRowClick,
    // then the rows are clickable, and apply any applicable styles/functionality
    const isRowClickable = typeof this.props.onRowClick === 'function';

    const unmodifiedTitle = !title && !!subtitle ? subtitle : title;

    const modifiedTitle = (
      <Fragment>
        {!!unmodifiedTitle && unmodifiedTitle}
        {isFiltered && typeof this.props.filterCountFormatter === 'function' ? (
          this.props.filterCountFormatter({ itemCount, totalCount })
        ) : (
          <p
            style={{
              display: !!subtitle ? 'inline-block' : 'block',
              borderLeft:
                !!dataType && !!subtitle ? '1px solid #a1a3a4' : 'none',
              fontSize: '12px',
              height: '19px',
              fontWeight: 'normal',
              color: '#a1a3a4',
              margin: !!subtitle ? '0 0 0 10px' : '0',
              paddingLeft: !!subtitle ? '10px' : '0'
            }}>
            {!!dataType && `Showing ${itemCount} of ${totalCount} ${dataType}`}
          </p>
        )}
      </Fragment>
    );

    return (
      <div className="DataTableEmbedded">
        {(!!additionalFilters || !isNoData) && (
          <div className="filterRow">
            {additionalFilters}
            {!!searchColumns && searchColumns.length > 0 && (
              <SearchInput
                onSearch={this.handleQueryChange}
                value={searchQuery}
                placeholder={searchPlaceholder || 'Search'}
              />
            )}
            {csvFilterCategories && (
              <CSVExport
                pageName={pageName}
                tableColumns={csvColumns || columns}
                filterCategories={csvFilterCategories}
                data={originalData}
                filename={csvCustomFileName}
              />
            )}
          </div>
        )}
        {!isNoData && (
          <FilterBox
            setQueryParams={this.props.setQueryParams}
            queryParams={this.props.queryParams}
            ErrorHandler={this.props.ErrorHandler}
            page="/storybook"
            filterCategories={filterCat}
            data={data}
            onChange={this.handleFilterChange}
          />
        )}
        {displayData.length ? (
          <div className="DataTableContent">
            <Table
              style={{
                minWidth: minTablePixelWidth
                  ? `${minTablePixelWidth}px`
                  : `${DEFAULT_CONTENT_MIN_WIDTH}px`
              }}
              {...tableProps}>
              <TableHeaderRow>
                {columns.map((d, index) => (
                  <TableHeaderCell
                    className="table-header-cell"
                    key={index}
                    style={Object.assign(
                      {},
                      d.style,
                      d.headerStyle,
                      d.width ? { width: d.width } : {}
                    )}
                    sorted={
                      sortColumn === d.dataKey
                        ? sortDirection === 'DESC'
                          ? 'descending'
                          : 'ascending'
                        : null
                    }
                    onClick={() => {
                      this.handleColumnHeaderClick(d.dataKey);
                    }}>
                    <HeaderInfoTooltip content={d.description}>
                      <span className="title">
                        {d.name}{' '}
                        {columnTotals && columnTotals.hasOwnProperty(d.dataKey)
                          ? `(${numberWithCommas(columnTotals[d.dataKey])})`
                          : null}
                      </span>
                    </HeaderInfoTooltip>
                  </TableHeaderCell>
                ))}
              </TableHeaderRow>
              <TableBody>
                {displayData.map((row, index) => (
                  <TableRow
                    {...rowProps}
                    key={index}
                    className={index === 0 ? 'first-table-row' : null}
                    style={
                      typeof this.props.rowStyleFormatter === 'function'
                        ? this.props.rowStyleFormatter(row, index)
                        : {}
                    }
                    onClick={() => {
                      this.handleRowClick(row);
                    }}>
                    {columns.map((col, colIndex) => (
                      <TableCell
                        {...(col.cellProps || {})}
                        key={colIndex}
                        style={Object.assign(
                          {
                            cursor: isRowClickable ? 'pointer' : 'default'
                          },
                          col.style,
                          col.cellStyle,
                          col.width ? { width: col.width } : {}
                        )}>
                        {typeof col.formatter === 'function'
                          ? col.formatter(getNestedValue(col.dataKey, row), row)
                          : getNestedValue(col.dataKey, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div
            className="well"
            style={{
              marginLeft: '50%',
              transform: 'translateX(-50%)'
            }}>
            {isNoData
              ? emptyStateMessage || 'No data found.'
              : noResultsMessage || 'No matches found'}
          </div>
        )}
        <TablePagination
          activePage={pageNumber}
          onPageChange={this.handlePageChange}
          itemCount={itemCount}
          perPage={PAGE_SIZE}
        />
      </div>
    );
  }
}


export default DataTable;

const Table = ({ children, className, ref, ...otherProps }) => (
  <div
    className={['Table', className].filter((f) => !!f).join(' ')}
    {...otherProps}>
    {children}
  </div>
);
const TableRow = ({ children, className, ...otherProps }) => (
  <div
    className={['TableRow', className].filter((f) => !!f).join(' ')}
    {...otherProps}>
    {children}
  </div>
);
const TableCell = ({ children, className, ...otherProps }) => (
  <div
    className={['TableCell', className].filter((f) => !!f).join(' ')}
    {...otherProps}>
    {children}
  </div>
);
const TableBody = ({ children, className }) => (
  <div className={['TableBody', className].filter((f) => !!f).join(' ')}>
    {children}
  </div>
);
const TableHeaderRow = ({ children, className }) => (
  <div className={['TableHeaderRow', className].filter((f) => !!f).join(' ')}>
    {children}
  </div>
);
const TableHeaderCell = ({ children, className, sorted, ...otherProps }) => (
  <div
    className={['TableHeaderCell', sorted && `sorted ${sorted}`, className]
      .filter((f) => !!f)
      .join(' ')}
    {...otherProps}>
    {children}
  </div>
);
